import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { useTheme } from '@mui/material/styles';

/**
 * @component DeliveryStatusPieChart
 * @description Pie chart showing delivery status breakdown (Delivered, Bounced, Failed)
 * @param {Object} props
 * @param {number} props.delivered - Number of delivered emails
 * @param {number} props.bounced - Number of bounced emails
 * @param {number} [props.failed=0] - Number of failed emails
 * @param {boolean} [props.loading=false] - Loading state
 * @param {number} [props.width=300] - Chart width
 * @param {number} [props.height=200] - Chart height
 * @returns {JSX.Element}
 */
export function DeliveryStatusPieChart({ 
  delivered = 0, 
  bounced = 0, 
  failed = 0, 
  loading = false,
  width = 300,
  height = 200 
}) {
  const theme = useTheme();

  const total = delivered + bounced + failed;

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', width, height }}>
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Paper>
    );
  }

  if (total === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', width, height }}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  const data = [
    { 
      id: 'delivered', 
      value: delivered, 
      label: 'Delivered',
      color: theme.palette.success.main
    },
    { 
      id: 'bounced', 
      value: bounced, 
      label: 'Bounced',
      color: theme.palette.warning.main
    },
    { 
      id: 'failed', 
      value: failed, 
      label: 'Failed',
      color: theme.palette.error.main
    }
  ].filter(item => item.value > 0);

  return (
    <Box sx={{ width, minHeight: height, pb: 2 }}>
      <PieChart
        series={[
          {
            data,
            innerRadius: 30,
            outerRadius: Math.min(width, height) / 2 - 10,
            paddingAngle: 2,
            cornerRadius: 5,
            highlightScope: { faded: 'global', highlighted: 'item' },
            faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
          },
        ]}
        width={width}
        height={height}
        slotProps={{
          legend: {
            direction: 'horizontal',
            position: { vertical: 'bottom', horizontal: 'center' },
            padding: 0,
            hidden: false,
          },
          tooltip: {
            trigger: 'item',
            formatter: (params) => {
              const item = data.find((d, idx) => idx === params.dataIndex);
              if (!item) return '';
              const percentage = ((item.value / total) * 100).toFixed(1);
              return `${item.label}: ${item.value} (${percentage}%)`;
            },
          },
        }}
        sx={{
          '& .MuiChartsLegend-root': {
            fontSize: '0.75rem',
          },
        }}
      />
    </Box>
  );
}

